import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Spinner, Select } from '../../../shared/components';
import { formatDate, formatDateTime } from '../../../shared/utils/localization';
import { listConsultationTypes } from '../../consultations';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import {
  type DashboardConversationChannelFilter,
  type DashboardSnapshotFilters,
  type DashboardTaskAssigneeFilter,
  loadDashboardSnapshot,
  type ConversationDashboardData,
  type ConsultationDashboardData,
  type DashboardActivityPoint,
  type DashboardModuleResult,
  type DashboardRange,
  type DashboardSnapshot,
  type TaskDashboardData,
} from '../services/dashboardService';
import styles from './DashboardPage.module.css';

type ConsultationTypeOption = {
  id: string;
  name: string;
};

type OverviewCardProps = {
  label: string;
  value: number;
  accent: 'blue' | 'teal' | 'gold';
  subtitle: string;
  to?: string;
};

type ModulePanelProps = {
  title: string;
  description: string;
  accent: 'blue' | 'teal' | 'gold';
  ctaLabel?: string;
  ctaTo?: string;
  error?: string;
  stats?: Array<{ label: string; value: string; hint: string; to?: string }>;
  breakdownTitle?: string;
  breakdown?: Array<{ label: string; count: number; to?: string }>;
  footer?: string;
};

type ActivitySeriesKey = 'total' | 'tasks' | 'conversations' | 'consultations';

type ActivitySeries = {
  key: ActivitySeriesKey;
  label: string;
  lineClassName: string;
  dotClassName: string;
  value: (point: DashboardActivityPoint) => number;
  to?: string;
};

type ActivityCoordinate = {
  key: string;
  label: string;
  value: number;
  x: number;
  y: number;
};

const DEFAULT_ACTIVITY_CHART_WIDTH = 720;
const ACTIVITY_CHART_HEIGHT = 300;
const ACTIVITY_CHART_PADDING = {
  top: 20,
  right: 20,
  bottom: 44,
  left: 42,
};
const ACTIVITY_GRID_ROWS = 4;
const ACTIVITY_STEP_MULTIPLIERS = [1, 2, 2.5, 5];
const ACTIVITY_MIN_GRID_ROWS = 1;
const ACTIVITY_MAX_GRID_ROWS = 6;
const ACTIVITY_TOOLTIP_FLIP_X_RATIO = 0.62;
const ACTIVITY_TOOLTIP_FLIP_Y_RATIO = 0.28;

/**
 * Picks an axis maximum and row count that keep every gridline on a distinct
 * whole number. The series are counts, so fractional ticks would render as
 * repeated labels (a max of 1 across 4 rows used to read "1, 1, 1, 0, 0").
 *
 * Step and row count are chosen together, because pinning the rows at four
 * forces a lot of dead headroom for values that do not divide by four.
 */
const buildActivityAxis = (maxValue: number, preferredRows: number) => {
  const safeMax = Math.max(1, Math.ceil(maxValue));
  const magnitudes = Array.from(
    { length: String(safeMax).length + 1 },
    (_, power) => 10 ** power,
  );

  let best = { axisMax: safeMax * preferredRows, rows: preferredRows };

  for (const magnitude of magnitudes) {
    for (const multiplier of ACTIVITY_STEP_MULTIPLIERS) {
      const step = multiplier * magnitude;
      if (!Number.isInteger(step)) {
        continue;
      }

      const rows = Math.ceil(safeMax / step);
      if (rows < ACTIVITY_MIN_GRID_ROWS || rows > ACTIVITY_MAX_GRID_ROWS) {
        continue;
      }

      const axisMax = step * rows;
      const isTighter = axisMax < best.axisMax;
      const isEqualButBetterShaped = axisMax === best.axisMax
        && Math.abs(rows - preferredRows) < Math.abs(best.rows - preferredRows);

      if (isTighter || isEqualButBetterShaped) {
        best = { axisMax, rows };
      }
    }
  }

  return best;
};

const getActivityTooltipStyle = (
  pointX: number,
  pointY: number,
  chartWidth: number,
): React.CSSProperties => {
  const leftRatio = pointX / chartWidth;
  const topRatio = pointY / ACTIVITY_CHART_HEIGHT;
  const flipX = leftRatio > ACTIVITY_TOOLTIP_FLIP_X_RATIO;
  const flipY = topRatio < ACTIVITY_TOOLTIP_FLIP_Y_RATIO;

  return {
    left: `${leftRatio * 100}%`,
    top: `${topRatio * 100}%`,
    transform: `translate(${flipX ? 'calc(-100% - 14px)' : '14px'}, ${flipY ? '14px' : 'calc(-100% - 14px)'})`,
  };
};

const formatCompactNumber = (value: number): string => {
  return new Intl.NumberFormat(undefined, {
    notation: value >= 1000 ? 'compact' : 'standard',
    maximumFractionDigits: 1,
  }).format(value);
};

const buildActivityCoordinates = (
  points: DashboardActivityPoint[],
  getValue: (point: DashboardActivityPoint) => number,
  maxValue: number,
  chartWidth: number,
): ActivityCoordinate[] => {
  if (points.length === 0) {
    return [];
  }

  const innerWidth = chartWidth - ACTIVITY_CHART_PADDING.left - ACTIVITY_CHART_PADDING.right;
  const innerHeight = ACTIVITY_CHART_HEIGHT - ACTIVITY_CHART_PADDING.top - ACTIVITY_CHART_PADDING.bottom;

  return points.map((point, index) => {
    const x = points.length === 1
      ? ACTIVITY_CHART_PADDING.left + (innerWidth / 2)
      : ACTIVITY_CHART_PADDING.left + ((index / (points.length - 1)) * innerWidth);
    const value = getValue(point);
    const y = ACTIVITY_CHART_PADDING.top + innerHeight - ((value / Math.max(1, maxValue)) * innerHeight);

    return {
      key: point.key,
      label: formatDate(point.key, { month: 'short', day: 'numeric' }),
      value,
      x,
      y,
    };
  });
};

const buildActivityPath = (points: ActivityCoordinate[]): string => {
  if (points.length === 0) {
    return '';
  }

  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
};

const buildDashboardRoute = (pathname: string, params: URLSearchParams): string => {
  const search = params.toString();
  return search ? `${pathname}?${search}` : pathname;
};

const buildTaskRoute = (
  filters: DashboardSnapshotFilters,
  options?: {
    status?: string;
    priority?: string;
  }
): string => {
  const params = new URLSearchParams();

  if (filters.taskAssignee === 'mine') {
    params.set('assignee', 'my');
  } else if (filters.taskAssignee === 'unassigned') {
    params.set('assignee', 'unassigned');
  }

  if (options?.status) {
    params.set('status', options.status);
  }

  if (options?.priority) {
    params.set('priority', options.priority);
  }

  return buildDashboardRoute('/tasks', params);
};

const buildConversationRoute = (view?: string): string => {
  const params = new URLSearchParams();
  if (view) {
    params.set('view', view);
  }

  return buildDashboardRoute('/conversations', params);
};

const buildConsultationRoute = (filters: DashboardSnapshotFilters): string => {
  const params = new URLSearchParams();

  if (filters.consultationTypeId) {
    params.set('type', filters.consultationTypeId);
  }

  return buildDashboardRoute('/consultations', params);
};

const OverviewCard: React.FC<OverviewCardProps> = ({ label, value, subtitle, accent, to }) => {
  const navigate = useNavigate();
  const handleClick = React.useCallback(() => {
    if (to) {
      navigate(to);
    }
  }, [navigate, to]);

  return (
    <button
      type="button"
      className={`${styles.metricCard} ${styles[`metricCard${accent[0].toUpperCase()}${accent.slice(1)}` as keyof typeof styles]} ${to ? styles.clickableCard : ''}`}
      onClick={to ? handleClick : undefined}
      disabled={!to}
    >
      <div className={styles.metricLabel}>{label}</div>
      <div className={styles.metricValue}>{formatCompactNumber(value)}</div>
      <div className={styles.metricSubtitle}>{subtitle}</div>
    </button>
  );
};

const ModulePanel: React.FC<ModulePanelProps> = ({
  title,
  description,
  accent,
  ctaLabel,
  ctaTo,
  error,
  stats = [],
  breakdownTitle,
  breakdown = [],
  footer,
}) => {
  const navigate = useNavigate();
  const maxBreakdown = breakdown.reduce((max, item) => Math.max(max, item.count), 0) || 1;
  const breakdownTotal = breakdown.reduce((sum, item) => sum + item.count, 0);
  const highlightedBreakdown = breakdown[0];
  const topBreakdownShare = breakdownTotal > 0 && highlightedBreakdown
    ? Math.round((highlightedBreakdown.count / breakdownTotal) * 100)
    : 0;

  return (
    <section className={styles.modulePanel}>
      <div className={styles.moduleHeader}>
        <div>
          <div className={`${styles.moduleEyebrow} ${styles[`moduleEyebrow${accent[0].toUpperCase()}${accent.slice(1)}` as keyof typeof styles]}`}>{title}</div>
          <h2 className={styles.moduleTitle}>{description}</h2>
        </div>
        {ctaLabel && ctaTo ? (
          <button type="button" className={styles.moduleHeaderAction} onClick={() => navigate(ctaTo)}>{ctaLabel}</button>
        ) : null}
      </div>

      {error ? (
        <div className={styles.moduleError}>{error}</div>
      ) : (
        <>
          <div className={styles.moduleStatGrid}>
            {stats.map((stat) => (
              <button
                key={stat.label}
                type="button"
                className={`${styles.moduleStatCard} ${stat.to ? styles.clickableCard : ''}`}
                onClick={stat.to ? () => navigate(stat.to!) : undefined}
                disabled={!stat.to}
              >
                <div className={styles.moduleStatLabel}>{stat.label}</div>
                <div className={styles.moduleStatValue}>{stat.value}</div>
                {stat.hint ? <div className={styles.moduleStatHint}>{stat.hint}</div> : null}
              </button>
            ))}
          </div>

          {breakdownTitle ? <div className={styles.breakdownTitle}>{breakdownTitle}</div> : null}
          {breakdown.length > 0 ? (
            <div className={styles.breakdownSection}>
              {highlightedBreakdown ? (
                <button
                  type="button"
                  className={`${styles.breakdownHeroCard} ${highlightedBreakdown.to ? styles.clickableCard : ''}`}
                  onClick={highlightedBreakdown.to ? () => navigate(highlightedBreakdown.to!) : undefined}
                  disabled={!highlightedBreakdown.to}
                >
                  <div>
                    <div className={styles.breakdownHeroEyebrow}>{title}</div>
                    <div className={styles.breakdownHeroLabel}>{highlightedBreakdown.label}</div>
                  </div>
                  <div className={styles.breakdownHeroValue}>{formatCompactNumber(highlightedBreakdown.count)}</div>
                  <div className={styles.breakdownHeroMeta}>
                    <span>{topBreakdownShare}% of visible mix</span>
                    <span>{formatCompactNumber(breakdownTotal)} total</span>
                  </div>
                  <div className={styles.breakdownHeroStack}>
                    {breakdown.slice(0, 5).map((item) => (
                      <span
                        key={`${title}-${item.label}`}
                        className={`${styles.breakdownHeroSegment} ${styles[`breakdownFill${accent[0].toUpperCase()}${accent.slice(1)}` as keyof typeof styles]}`}
                        style={{ width: `${Math.max(8, Math.round((item.count / Math.max(1, breakdownTotal)) * 100))}%` }}
                      />
                    ))}
                  </div>
                </button>
              ) : null}

              <div className={styles.breakdownList}>
                {breakdown.slice(0, 5).map((item, index) => {
                  const share = breakdownTotal > 0 ? Math.round((item.count / breakdownTotal) * 100) : 0;

                  return (
                    <button
                      key={item.label}
                      type="button"
                      className={`${styles.breakdownRow} ${item.to ? styles.clickableBreakdownRow : ''}`}
                      onClick={item.to ? () => navigate(item.to!) : undefined}
                      disabled={!item.to}
                    >
                      <div className={styles.breakdownRowHeader}>
                        <div className={styles.breakdownRowLabelGroup}>
                          <span className={styles.breakdownRank}>{String(index + 1).padStart(2, '0')}</span>
                          <span className={styles.breakdownLabel}>{item.label}</span>
                        </div>
                        <div className={styles.breakdownMetrics}>
                          <span className={styles.breakdownShare}>{share}%</span>
                          <span className={styles.breakdownValue}>{formatCompactNumber(item.count)}</span>
                        </div>
                      </div>
                      <div className={styles.breakdownTrack}>
                        <div
                          className={`${styles.breakdownFill} ${styles[`breakdownFill${accent[0].toUpperCase()}${accent.slice(1)}` as keyof typeof styles]}`}
                          style={{ width: `${Math.max(10, Math.round((item.count / maxBreakdown) * 100))}%` }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {footer ? <div className={styles.moduleFooter}>{footer}</div> : null}
        </>
      )}
    </section>
  );
};

const buildTaskPanel = (
  t: ReturnType<typeof useTranslation>['t'],
  module: DashboardModuleResult<TaskDashboardData> | undefined,
  filters: DashboardSnapshotFilters
): ModulePanelProps | null => {
  if (!module) {
    return null;
  }

  const data = module.data;
  return {
    title: t('tasks', { ns: 'nav' }),
    description: t('dashboardTaskDescription'),
    accent: 'blue',
    ctaLabel: t('dashboardOpenTasks'),
    ctaTo: buildTaskRoute(filters),
    error: module.error,
    stats: data ? [
      {
        label: t('dashboardTotalTasks'),
        value: formatCompactNumber(data.totalCount),
        hint: '',
        to: buildTaskRoute(filters),
      },
      {
        label: t('dashboardCompleted'),
        value: formatCompactNumber(data.completedCount),
        hint: data.totalCount > 0 ? `${Math.round((data.completedCount / data.totalCount) * 100)}%` : '0%',
        to: buildTaskRoute(filters, { status: 'completed' }),
      },
      {
        label: t('dashboardOverdue'),
        value: formatCompactNumber(data.overdueCount),
        hint: '',
      },
      {
        label: t('dashboardHighPriority'),
        value: formatCompactNumber(data.highPriorityCount),
        hint: t('dashboardAssignedCount', { count: data.assignedCount }),
        to: buildTaskRoute(filters, { priority: 'high' }),
      },
    ] : [],
    breakdownTitle: t('dashboardStatusMix'),
    breakdown: data?.statusBreakdown,
  };
};

const buildConversationPanel = (t: ReturnType<typeof useTranslation>['t'], module: DashboardModuleResult<ConversationDashboardData> | undefined): ModulePanelProps | null => {
  if (!module) {
    return null;
  }

  const data = module.data;
  return {
    title: t('conversations', { ns: 'nav' }),
    description: t('dashboardConversationDescription'),
    accent: 'teal',
    ctaLabel: t('dashboardOpenConversations'),
    ctaTo: buildConversationRoute(),
    error: module.error,
    stats: data ? [
      {
        label: t('dashboardTotalConversations'),
        value: formatCompactNumber(data.totalCount),
        hint: '',
        to: buildConversationRoute(),
      },
      {
        label: t('dashboardDirect'),
        value: formatCompactNumber(data.directCount),
        hint: t('dashboardChannelCount', { count: data.channelCount }),
        to: buildConversationRoute('direct'),
      },
      {
        label: t('dashboardExternal'),
        value: formatCompactNumber(data.externalCount),
        hint: t('dashboardWhatsappCount', { count: data.whatsAppCount }),
        to: buildConversationRoute('external'),
      },
      {
        label: t('dashboardAiMuted'),
        value: formatCompactNumber(data.aiMutedCount),
        hint: '',
      },
    ] : [],
    breakdownTitle: t('dashboardConversationMix'),
    breakdown: data?.kindBreakdown.map((item) => ({
      ...item,
      label: item.label === 'Direct'
        ? t('dashboardDirect')
        : item.label === 'Channels'
          ? t('dashboardChannels')
          : item.label === 'External'
            ? t('dashboardExternal')
            : item.label,
      to: item.label === 'Direct'
        ? buildConversationRoute('direct')
        : item.label === 'Channels'
          ? buildConversationRoute('channels')
          : item.label === 'External'
            ? buildConversationRoute('external')
            : undefined,
    })),
  };
};

const buildConsultationPanel = (
  t: ReturnType<typeof useTranslation>['t'],
  module: DashboardModuleResult<ConsultationDashboardData> | undefined,
  filters: DashboardSnapshotFilters
): ModulePanelProps | null => {
  if (!module) {
    return null;
  }

  const data = module.data;
  return {
    title: t('consultations', { ns: 'nav' }),
    description: t('dashboardConsultationDescription'),
    accent: 'gold',
    ctaLabel: t('dashboardOpenConsultations'),
    ctaTo: buildConsultationRoute(filters),
    error: module.error,
    stats: data ? [
      {
        label: t('dashboardTotalConsultations'),
        value: formatCompactNumber(data.totalCount),
        hint: '',
        to: buildConsultationRoute(filters),
      },
      {
        label: t('dashboardUpcoming'),
        value: formatCompactNumber(data.upcomingCount),
        hint: '',
      },
      {
        label: t('dashboardCompleted'),
        value: formatCompactNumber(data.completedCount),
        hint: t('dashboardStatusCaptured', { count: data.withStatusCount }),
      },
      {
        label: t('dashboardAiCreated'),
        value: formatCompactNumber(data.aiCreatedCount),
        hint: '',
      },
    ] : [],
    breakdownTitle: t('dashboardConsultationStatusMix'),
    breakdown: data?.statusBreakdown,
  };
};

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation(['common', 'nav']);
  const navigate = useNavigate();
  const { hasPermission, auth } = useAuth();
  const canViewTasks = hasPermission(PermissionScopes.tasksRead);
  const canViewConversations = hasPermission(PermissionScopes.conversationsRead);
  const canViewConsultations = hasPermission(PermissionScopes.consultationsRead);
  const currentEmployeeId = auth?.employeeId || undefined;
  const [range, setRange] = React.useState<DashboardRange>('7d');
  const [taskAssigneeFilter, setTaskAssigneeFilter] = React.useState<DashboardTaskAssigneeFilter>('all');
  const [conversationChannelFilter, setConversationChannelFilter] = React.useState<DashboardConversationChannelFilter>('all');
  const [consultationTypeFilter, setConsultationTypeFilter] = React.useState<string>('all');
  const [consultationTypeOptions, setConsultationTypeOptions] = React.useState<ConsultationTypeOption[]>([]);
  const [snapshot, setSnapshot] = React.useState<DashboardSnapshot | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [activeSeries, setActiveSeries] = React.useState<ActivitySeriesKey>('total');
  const [hoveredActivityIndex, setHoveredActivityIndex] = React.useState<number | null>(null);
  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = React.useState(DEFAULT_ACTIVITY_CHART_WIDTH);

  React.useLayoutEffect(() => {
    const element = chartContainerRef.current;
    if (!element) {
      return;
    }

    const updateChartWidth = () => {
      const nextWidth = Math.floor(element.clientWidth);
      if (nextWidth > 0) {
        setChartWidth(nextWidth);
      }
    };

    updateChartWidth();

    const observer = new ResizeObserver(updateChartWidth);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  React.useEffect(() => {
    let active = true;

    if (!canViewConsultations) {
      setConsultationTypeOptions([]);
      return () => {
        active = false;
      };
    }

    void listConsultationTypes()
      .then((items) => {
        if (!active) {
          return;
        }

        setConsultationTypeOptions(items.map((item) => ({ id: item.id, name: item.name })));
      })
      .catch(() => {
        if (active) {
          setConsultationTypeOptions([]);
        }
      });

    return () => {
      active = false;
    };
  }, [canViewConsultations]);

  const snapshotFilters = React.useMemo<DashboardSnapshotFilters>(() => ({
    taskAssignee: taskAssigneeFilter,
    taskAssigneeId: taskAssigneeFilter === 'mine' ? currentEmployeeId : undefined,
    conversationChannel: conversationChannelFilter,
    consultationTypeId: consultationTypeFilter !== 'all' ? consultationTypeFilter : undefined,
  }), [consultationTypeFilter, conversationChannelFilter, currentEmployeeId, taskAssigneeFilter]);

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    void loadDashboardSnapshot({
      range,
      canViewTasks,
      canViewConversations,
      canViewConsultations,
      filters: snapshotFilters,
    })
      .then((nextSnapshot) => {
        if (!active) {
          return;
        }

        setSnapshot(nextSnapshot);
      })
      .catch((requestError) => {
        if (!active) {
          return;
        }

        setError(requestError instanceof Error ? requestError.message : t('dashboardLoadFailed'));
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [canViewConsultations, canViewConversations, canViewTasks, range, snapshotFilters, t]);

  const activitySeries: ActivitySeries[] = [
    {
      key: 'total',
      label: t('dashboardTotal'),
      lineClassName: styles.activityLineTotal,
      dotClassName: styles.activityDotTotal,
      value: (point) => point.total,
    },
    {
      key: 'tasks',
      label: t('tasks', { ns: 'nav' }),
      lineClassName: styles.activityLineTasks,
      dotClassName: styles.activityDotBlue,
      value: (point) => point.tasks,
      to: buildTaskRoute(snapshotFilters),
    },
    {
      key: 'conversations',
      label: t('conversations', { ns: 'nav' }),
      lineClassName: styles.activityLineConversations,
      dotClassName: styles.activityDotTeal,
      value: (point) => point.conversations,
      to: buildConversationRoute(),
    },
    {
      key: 'consultations',
      label: t('consultations', { ns: 'nav' }),
      lineClassName: styles.activityLineConsultations,
      dotClassName: styles.activityDotGold,
      value: (point) => point.consultations,
      to: buildConsultationRoute(snapshotFilters),
    },
  ];

  if (loading && !snapshot) {
    return (
      <div className={styles.loadingState}>
        <Spinner />
      </div>
    );
  }

  if (error && !snapshot) {
    return <div className={styles.pageError}>{error}</div>;
  }

  const activity = snapshot?.activity ?? [];
  const taskPanel = buildTaskPanel(t, snapshot?.tasks, snapshotFilters);
  const conversationPanel = buildConversationPanel(t, snapshot?.conversations);
  const consultationPanel = buildConsultationPanel(t, snapshot?.consultations, snapshotFilters);
  const visibleActivitySeries = activitySeries.filter((series) => {
    if (series.key === 'tasks') {
      return canViewTasks;
    }

    if (series.key === 'conversations') {
      return canViewConversations;
    }

    if (series.key === 'consultations') {
      return canViewConsultations;
    }

    return true;
  });
  const resolvedActiveSeries = visibleActivitySeries.some((series) => series.key === activeSeries)
    ? activeSeries
    : 'total';
  const activeActivitySeries = visibleActivitySeries.find((series) => series.key === resolvedActiveSeries);
  const activityMaxValue = activity.reduce((max, point) => {
    return Math.max(max, ...visibleActivitySeries.map((series) => series.value(point)));
  }, 0) || 1;
  const activityAxis = buildActivityAxis(activityMaxValue, ACTIVITY_GRID_ROWS);
  const activityCoordinates = visibleActivitySeries.map((series) => ({
    ...series,
    points: buildActivityCoordinates(activity, series.value, activityAxis.axisMax, chartWidth),
  }));
  const hoverIndex = hoveredActivityIndex;
  const hoveredPoint = hoverIndex !== null ? activity[hoverIndex] : null;
  const hoveredSeriesPoint = hoverIndex !== null
    ? activityCoordinates.find((series) => series.key === resolvedActiveSeries)?.points[hoverIndex] ?? null
    : null;
  const innerChartWidth = chartWidth - ACTIVITY_CHART_PADDING.left - ACTIVITY_CHART_PADDING.right;
  const innerChartHeight = ACTIVITY_CHART_HEIGHT - ACTIVITY_CHART_PADDING.top - ACTIVITY_CHART_PADDING.bottom;
  const activityBandWidth = activity.length > 1
    ? innerChartWidth / (activity.length - 1)
    : innerChartWidth;
  const xAxisLabelStep = activity.length > 10 ? Math.ceil(activity.length / 6) : 1;
  const activityGridLines = Array.from({ length: activityAxis.rows + 1 }, (_, index) => {
    const ratio = index / activityAxis.rows;
    return {
      y: ACTIVITY_CHART_PADDING.top + (innerChartHeight * ratio),
      value: Math.round(activityAxis.axisMax * (1 - ratio)),
    };
  });
  const overviewCards: OverviewCardProps[] = [
    canViewTasks && snapshot?.tasks?.data ? {
      label: t('tasks', { ns: 'nav' }),
      value: snapshot.tasks.data.totalCount,
      accent: 'blue' as const,
      to: '/tasks',
      subtitle: t('dashboardCompletedHint', {
        count: snapshot.tasks.data.completedCount,
        defaultValue: '{{count}} completed',
      }),
    } : null,
    canViewConversations && snapshot?.conversations?.data ? {
      label: t('conversations', { ns: 'nav' }),
      value: snapshot.conversations.data.totalCount,
      accent: 'teal' as const,
      to: '/conversations',
      subtitle: t('dashboardExternalHint', {
        count: snapshot.conversations.data.externalCount,
        defaultValue: '{{count}} external',
      }),
    } : null,
    canViewConsultations && snapshot?.consultations?.data ? {
      label: t('consultations', { ns: 'nav' }),
      value: snapshot.consultations.data.totalCount,
      accent: 'gold' as const,
      to: '/consultations',
      subtitle: t('dashboardUpcomingHint', {
        count: snapshot.consultations.data.upcomingCount,
        defaultValue: '{{count}} upcoming',
      }),
    } : null,
  ].filter(Boolean) as OverviewCardProps[];

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div>
          <div className={styles.heroEyebrow}>{t('dashboardLabel')}</div>
          <h1 className={styles.heroTitle}>{t('dashboardTitle')}</h1>
        </div>

        <div className={styles.heroControls}>
          <div className={styles.rangeToggle}>
            <button
              type="button"
              className={range === '7d' ? styles.rangeToggleActive : styles.rangeToggleButton}
              onClick={() => setRange('7d')}
            >
              7D
            </button>
            <button
              type="button"
              className={range === '30d' ? styles.rangeToggleActive : styles.rangeToggleButton}
              onClick={() => setRange('30d')}
            >
              30D
            </button>
          </div>
          <div className={styles.generatedAt}>
            {snapshot?.generatedAt ? t('dashboardUpdatedAt', { value: formatDateTime(snapshot.generatedAt) }) : null}
          </div>
        </div>
      </section>

      <section className={styles.filterPanel}>
        {canViewTasks ? (
          <label className={styles.filterField}>
            <span>{t('dashboardTaskOwner')}</span>
            <Select
              value={taskAssigneeFilter}
              onChange={(nextValue) => setTaskAssigneeFilter(nextValue as DashboardTaskAssigneeFilter)}
              options={[
                { value: 'all', label: t('dashboardFilterAll') },
                { value: 'mine', label: t('dashboardFilterMine') },
                { value: 'unassigned', label: t('dashboardFilterUnassigned') },
              ]}
            />
          </label>
        ) : null}

        {canViewConversations ? (
          <label className={styles.filterField}>
            <span>{t('dashboardConversationChannel')}</span>
            <Select
              value={conversationChannelFilter}
              onChange={(nextValue) => setConversationChannelFilter(nextValue as DashboardConversationChannelFilter)}
              options={[
                { value: 'all', label: t('dashboardFilterAll') },
                { value: 'whatsApp', label: t('dashboardWhatsApp') },
                { value: 'telegram', label: t('telegram') },
                { value: 'instagram', label: t('instagram') },
              ]}
            />
          </label>
        ) : null}

        {canViewConsultations ? (
          <label className={styles.filterField}>
            <span>{t('dashboardConsultationType')}</span>
            <Select
              value={consultationTypeFilter}
              onChange={setConsultationTypeFilter}
              options={[
                { value: 'all', label: t('dashboardFilterAll') },
                ...consultationTypeOptions.map((option) => ({
                  value: option.id,
                  label: option.name,
                })),
              ]}
            />
          </label>
        ) : null}
      </section>

      {loading ? <div className={styles.refreshing}>{t('dashboardRefreshing')}</div> : null}
      {error ? <div className={styles.inlineError}>{error}</div> : null}

      <section className={`${styles.activityPanel} ${styles.activityPanelFeatured}`}>
        <div className={styles.activityHeader}>
          <div>
            <div className={styles.activityEyebrow}>{t('dashboardSharedActivity')}</div>
            <h2 className={styles.activityTitle}>{t('dashboardActivityTitle')}</h2>
          </div>
          <div className={styles.activityLegend}>
            {visibleActivitySeries.map((series) => (
              <button
                key={series.key}
                type="button"
                className={`${styles.activityLegendButton} ${resolvedActiveSeries === series.key ? styles.activityLegendButtonActive : ''}`}
                onClick={() => setActiveSeries(series.key)}
              >
                <i
                  className={`${styles.legendDot} ${
                    series.key === 'total'
                      ? styles.legendDotTotal
                      : series.key === 'tasks'
                        ? styles.legendDotBlue
                        : series.key === 'conversations'
                          ? styles.legendDotTeal
                          : styles.legendDotGold
                  }`}
                />
                {series.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.activityChartShell} ref={chartContainerRef}>
          {activeActivitySeries?.to ? (
            <div className={styles.activityActionRow}>
              <button type="button" className={styles.moduleHeaderAction} onClick={() => navigate(activeActivitySeries.to!)}>
                {t('dashboardOpenSeries', {
                  label: activeActivitySeries.label.toLowerCase(),
                  defaultValue: `Open ${activeActivitySeries.label.toLowerCase()}`,
                })}
              </button>
            </div>
          ) : null}

          <div className={styles.activityChartViewport}>
          <svg
            viewBox={`0 0 ${chartWidth} ${ACTIVITY_CHART_HEIGHT}`}
            className={styles.activityChart}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={t('dashboardActivityTitle')}
            onMouseLeave={() => setHoveredActivityIndex(null)}
          >
            {activityGridLines.map((line) => (
              <g key={line.y}>
                <line
                  x1={ACTIVITY_CHART_PADDING.left}
                  x2={chartWidth - ACTIVITY_CHART_PADDING.right}
                  y1={line.y}
                  y2={line.y}
                  className={styles.activityGridLine}
                />
                <text x={10} y={line.y + 4} className={styles.activityAxisLabel}>{formatCompactNumber(line.value)}</text>
              </g>
            ))}

            {activityCoordinates.map((series) => (
              <path
                key={series.key}
                d={buildActivityPath(series.points)}
                className={series.lineClassName}
                style={{ opacity: resolvedActiveSeries === series.key ? 1 : 0.26 }}
              />
            ))}

            {hoveredSeriesPoint ? (
              <line
                x1={hoveredSeriesPoint.x}
                x2={hoveredSeriesPoint.x}
                y1={ACTIVITY_CHART_PADDING.top}
                y2={ACTIVITY_CHART_HEIGHT - ACTIVITY_CHART_PADDING.bottom}
                className={styles.activityHoverLine}
              />
            ) : null}

            {activityCoordinates.map((series) => (
              <g key={`${series.key}-points`}>
                {series.points.map((point, index) => (
                  <circle
                    key={`${series.key}-${point.key}`}
                    cx={point.x}
                    cy={point.y}
                    r={hoverIndex === index && resolvedActiveSeries === series.key ? 5 : resolvedActiveSeries === series.key ? 3.5 : 0}
                    className={series.dotClassName}
                  />
                ))}
              </g>
            ))}

            {activityCoordinates[0]?.points.map((point, index) => {
              const x = index === 0 ? ACTIVITY_CHART_PADDING.left : point.x - (activityBandWidth / 2);
              const width = activity.length === 1
                ? innerChartWidth
                : index === activity.length - 1
                  ? (chartWidth - ACTIVITY_CHART_PADDING.right) - x
                  : activityBandWidth;

              return (
                <rect
                  key={point.key}
                  x={x}
                  y={ACTIVITY_CHART_PADDING.top}
                  width={Math.max(1, width)}
                  height={innerChartHeight}
                  className={styles.activityHitArea}
                  onMouseEnter={() => setHoveredActivityIndex(index)}
                  onFocus={() => setHoveredActivityIndex(index)}
                  onTouchStart={() => setHoveredActivityIndex(index)}
                  onClick={() => setHoveredActivityIndex(index)}
                />
              );
            })}

            {activityCoordinates[0]?.points.map((point, index) => {
              if (index % xAxisLabelStep !== 0 && index !== activity.length - 1) {
                return null;
              }

              return (
                <text
                  key={`${point.key}-label`}
                  x={point.x}
                  y={ACTIVITY_CHART_HEIGHT - 14}
                  textAnchor="middle"
                  className={styles.activityAxisLabel}
                >
                  {point.label}
                </text>
              );
            })}
          </svg>

          {hoveredPoint && hoveredSeriesPoint ? (
            <div
              className={styles.activityTooltip}
              style={getActivityTooltipStyle(hoveredSeriesPoint.x, hoveredSeriesPoint.y, chartWidth)}
            >
              <div className={styles.activityTooltipTitle}>
                {formatDate(hoveredPoint.key, { month: 'short', day: 'numeric' })}
              </div>
              <div className={styles.activityTooltipRows}>
                {visibleActivitySeries.map((series) => (
                  <div key={`${series.key}-tooltip`} className={styles.activityTooltipRow}>
                    <span className={styles.activityTooltipSeries}>
                      <span className={`${styles.activityTooltipDot} ${series.dotClassName}`} aria-hidden="true" />
                      <span className={styles.activityTooltipLabel}>{series.label}</span>
                    </span>
                    <span className={styles.activityTooltipValue}>
                      {formatCompactNumber(series.value(hoveredPoint))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          </div>
        </div>
      </section>

      <section className={styles.overviewGrid}>
        {overviewCards.map((card) => (
          <OverviewCard key={card.label} {...card} />
        ))}
      </section>

      <div className={styles.moduleGrid}>
        {taskPanel ? <ModulePanel {...taskPanel} /> : null}
        {conversationPanel ? <ModulePanel {...conversationPanel} /> : null}
        {consultationPanel ? <ModulePanel {...consultationPanel} /> : null}
      </div>
    </div>
  );
};